/**
 * Configuration du front : /config.json (ConfigFront, généré au déploiement à partir des sorties CloudFormation).
 *
 * Mode maquette (aucun appel réseau, données simulées) – pour développer le front sans backend :
 *   - `?maquette=1` dans l'URL (mémorisé pour l'onglet ; `?maquette=0` pour en sortir),
 *   - ou `VITE_MAQUETTE=1 npm run dev -w web`,
 *   - ou automatiquement si /config.json est absent (copier web/config.exemple.json en web/public/config.json pour un vrai backend).
 */
import type { ConfigFront } from '@agora/shared';

export interface ConfigApp extends ConfigFront {
  maquette: boolean;
}

const CLE_MAQUETTE = 'agora.maquette';

const CONFIG_MAQUETTE: ConfigFront = {
  env: 'dev',
  apiUrl: '',
  region: 'eu-west-3',
  cognito: { userPoolId: 'maquette', clientId: 'maquette', domaine: 'maquette.invalid', fournisseurSso: 'EntraID' },
  connexion: { sso: true, secoursEmail: true },
  version: 'maquette',
};

function maquetteDemandee(): boolean | undefined {
  try {
    const q = new URLSearchParams(window.location.search).get('maquette');
    if (q === '1') sessionStorage.setItem(CLE_MAQUETTE, '1');
    if (q === '0') sessionStorage.removeItem(CLE_MAQUETTE);
    if (sessionStorage.getItem(CLE_MAQUETTE) === '1') return true;
  } catch { /* stockage indisponible */ }
  if (import.meta.env.VITE_MAQUETTE === '1') return true;
  return undefined;
}

function estConfig(v: unknown): v is ConfigFront {
  const c = v as ConfigFront;
  return !!c && typeof c === 'object' && typeof c.apiUrl === 'string' && !!c.cognito && typeof c.cognito.clientId === 'string' && !!c.connexion;
}

/**
 * Jamais de maquette en production ; repli automatique sur la maquette (config absente) uniquement avec le
 * serveur de développement Vite – un site déployé sans configuration affiche une erreur, pas des données simulées.
 */
export async function chargerConfig(): Promise<ConfigApp> {
  const demandee = maquetteDemandee();
  let config: ConfigFront | undefined;
  try {
    const r = await fetch('/config.json', { cache: 'no-store', headers: { Accept: 'application/json' } });
    const type = r.headers.get('content-type') || '';
    if (r.ok && type.includes('json')) {
      const c: unknown = await r.json();
      if (estConfig(c)) config = { ...c, apiUrl: c.apiUrl.replace(/\/+$/, '') };
    }
  } catch { /* pas de configuration */ }
  if (config?.env === 'prod') return { ...config, maquette: false };
  if (demandee) return { ...CONFIG_MAQUETTE, maquette: true };
  if (config) return { ...config, maquette: false };
  if (import.meta.env.DEV) {
    console.info('[AGORA] /config.json absent ou invalide : mode maquette (données simulées).');
    return { ...CONFIG_MAQUETTE, maquette: true };
  }
  throw new Error('/config.json absent ou invalide');
}
